import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'
import {
  activeDaysOf,
  firstOfMonth,
  isoDayOf,
  lastOfMonth,
  cashierChipsOf,
  kindChipsOf,
  spanDays,
  statCardsOf,
  trendOf,
  type ActiveDay,
  type TrendPoint,
  type ReceiptsDigest,
} from '@krp/ui-kit'

import { formatInstant, formatKopecks, formatQuantity } from '../../../shared/uc1b/format'
import { effectiveFilter, listShownOf } from './effective'
import { attentionLines, LATE_PAYMENT_SLIP, MONEY, RECEIPT } from '../../sale/model/selectors'
import type { AqsiAttentionSale, AqsiSale } from '../../sale/model/types'
import type { CommandFeedback } from '../../../shared/model/feedback'
import { EMPTY_RECEIPTS_VIEW } from './receiptsSlice'
import { selectionText, selectedNodeOf, shopNode, treeNodesOf, NO_SHOP } from './tree'
import type {
  ParkedRowVm,
  ParkedVm,
  AttentionRowVm,
  AttentionVm,
  JournalJob,
  ReceiptDetail,
  ReceiptDetailVm,
  ReceiptItem,
  ReceiptLineVm,
  ReceiptPage,
  ReceiptRegisters,
  ReceiptRowVm,
  ReceiptShifts,
  ReceiptsFilter,
  ReceiptsIntentState,
  ReceiptsJournalVm,
  ReceiptsNavVm,
  ReceiptsPeriodVm,
  ReceiptsStatsVm,
  ReceiptsView,
  ReceiptsVm,
  ReceiptSummary,
  SyncFailure,
} from './types'

/**
 * Именованные селекторы журнала чеков (D-061, D-062). Вход — НЕ RootState, а то,
 * что binding выбрал: результаты запросов и намерение фичи. Пакет компилируется
 * и тестируется без приложения (F03).
 */
export interface ReceiptsVmInput {
  resourceId: string
  view: ReceiptsView
  list: QueryResultLike<ReceiptPage>
  registers: QueryResultLike<ReceiptRegisters>
  shifts: QueryResultLike<ReceiptShifts>
  /** Итоги по отбору без вида чека (`aqsiReceiptSummary` с `summaryFilterOf`). */
  summary: QueryResultLike<ReceiptSummary>
  monthSummary?: QueryResultLike<ReceiptSummary>
  /** Сегодняшний день человека, `ГГГГ-ММ-ДД`: пустые даты отбора — это он. */
  today: string
  journal: QueryResultLike<JournalJob | null>
  syncFailure: SyncFailure | null
  syncing?: boolean
  /** «Требуют внимания» (`aqsiSalesAttention`). Не передано — binding вкладку не подключил. */
  attention?: QueryResultLike<readonly AqsiAttentionSale[]>
  /** Есть право `sales.read` в этом кабинете; не передано — не запрещать (право всё равно проверит служба, 403 — словами). */
  attentionAllowed?: boolean
  /** Отложенные продажи ресурса (`aqsiParkedSales`). Не передано — binding вкладку не подключил. */
  parked?: QueryResultLike<readonly AqsiSale[]>
  /** Моя касса за кассой есть (нужна для «Вернуть на кассу»); нет — кнопка заблокирована словами. */
  seatReady?: boolean
  /** Итог последней команды «Вернуть на кассу» (feedback кассы). */
  unparkFeedback?: CommandFeedback | undefined
}

export interface ReceiptDetailVmInput {
  query: QueryResultLike<ReceiptDetail>
  /**
   * UC1d: служба считает возвращённое по чеку (`capabilities.refunds` журнала
   * этого rid). Детализация сама capabilities не несёт — их даёт binding из
   * ответа журнала; не известно — `undefined`, и 0 не выдаётся за «возвратов нет».
   */
  refundsKnown?: boolean
}

export const CURSOR_ERROR_CODES: ReadonlySet<string> = new Set([
  'cursor-invalid',
  'cursor-expired',
  'cursor-context-mismatch',
  'cursor-scope-mismatch',
])

export const selectReceiptsView = (intent: ReceiptsIntentState, resourceId: string): ReceiptsView =>
  intent.byResource[resourceId] ?? EMPTY_RECEIPTS_VIEW

export const selectSyncFailure = (intent: ReceiptsIntentState, resourceId: string): SyncFailure | null =>
  intent.syncError[resourceId] ?? null

/** Курсор текущей страницы: у первой нет курсора. */
export function cursorOf(view: ReceiptsView): string | null {
  return view.page === 0 ? null : (view.cursors[view.page - 1] ?? null)
}

const FILTER_KEYS = ['registerId', 'shop', 'cashier', 'shiftNumber', 'dateFrom', 'dateTo', 'search'] as const

function sameFilter(left: ReceiptsFilter, right: ReceiptsFilter): boolean {
  return FILTER_KEYS.every((key) => left[key] === right[key])
}

const EMPTY_ROWS: readonly ReceiptRowVm[] = []
const NO_COLLAPSED: readonly string[] = []

/** Итоги AQSI (считает сервер, копейки по модулю) → общая сводка чеков. Карточки, чипы и дни строит общий слой ui-kit. */
function digestOf(data: ReceiptSummary): ReceiptsDigest {
  const detail = (cash: number, cashless: number) => `нал. ${formatKopecks(cash)} · безнал. ${formatKopecks(cashless)}`
  return {
    sales: { count: data.saleCount, sum: data.saleKopecks, detail: detail(data.saleCashKopecks, data.saleCashlessKopecks), cash: data.saleCashKopecks, cashless: data.saleCashlessKopecks },
    refunds: { count: data.refundCount, sum: data.refundKopecks, detail: detail(data.refundCashKopecks, data.refundCashlessKopecks), cash: data.refundCashKopecks, cashless: data.refundCashlessKopecks },
    days: data.days.map((item) => ({ day: item.day, saleCount: item.saleCount, saleSum: item.saleKopecks, refundCount: item.refundCount, refundSum: item.refundKopecks })),
    cashiers: data.cashiers,
  }
}

/** Сколько дней в периоде отбора; `null` — выбрана смена, её время задаёт она сама. */
function periodDaysOf(applied: ReceiptsFilter, today: string): number | null {
  const effective = effectiveFilter(applied, today)
  return effective.dateFrom === '' ? null : spanDays(effective.dateFrom, effective.dateTo)
}

const NO_DAYS: readonly ActiveDay[] = []
const NO_TREND: readonly TrendPoint[] = []

function paymentsDiffer(item: { totalKopecks: number; cashKopecks: number; cashlessKopecks: number }): boolean {
  return item.cashKopecks + item.cashlessKopecks !== item.totalKopecks
}

function saleTextOf(item: ReceiptItem): string {
  // Номер продажи — служебный UUID; кассиру важно, что чек пробит из РМК (аудит 15.10, п. 18).
  return item.saleId !== null ? 'Продажа из РМК' : 'Локальной продажи нет'
}

function confirmTextOf(item: ReceiptItem): string {
  return item.confirmed ? 'Подтверждён кабинетом' : 'Не подтверждён кабинетом (только локальный журнал)'
}

/**
 * Откуда показаны чеки (RP-FB41): кабинет AQSI со временем чтения или журнал программы. Время — по часам человека, с секундами:
 * по нему видно, свежий ли ответ после «Обновить».
 */
export function originTextOf(origin: string | null | undefined, readAt: string | null | undefined): string | null {
  const at = readAt === null || readAt === undefined ? '' : `, прочитано в ${formatInstant(readAt)}`
  switch (origin) {
    case 'cabinet-shift':
      return `Закрытая смена — из кабинета AQSI${at}`
    case 'cabinet-period':
      return `Из кабинета AQSI и журнала программы${at}`
    case 'cabinet':
      return `Чек из кабинета AQSI${at}: в журнале программы его нет`
    case 'buffer':
      return 'Из журнала программы (открытая смена или чеки без периода)'
    default:
      return null
  }
}

function toRow(item: ReceiptItem, refundsKnown: boolean): ReceiptRowVm {
  return {
    id: item.id,
    refund: item.isRefund,
    number: item.docNumber === null ? '' : String(item.docNumber),
    at: formatInstant(item.at),
    register: item.deviceSerialNumber,
    total: item.totalKopecks / 100,
    cash: item.cashKopecks / 100,
    card: item.cashlessKopecks / 100,
    atText: formatInstant(item.at),
    kind: item.isRefund ? 'refund' : 'sale',
    kindText: item.isRefund ? 'Возврат' : 'Продажа',
    totalText: formatKopecks(item.totalKopecks),
    cashText: formatKopecks(item.cashKopecks),
    cashlessText: formatKopecks(item.cashlessKopecks),
    payment: { total: item.totalKopecks / 100, cash: item.cashKopecks / 100, card: item.cashlessKopecks / 100 },
    shiftText: item.shiftNumber === null ? 'смена не указана' : `смена ${item.shiftNumber}`,
    docText: item.docNumber === null ? 'документ не указан' : `документ ${item.docNumber}`,
    cashier: item.cashierName === '' ? 'кассир не указан' : item.cashierName,
    device: item.deviceSerialNumber === '' ? 'касса не указана' : `касса ${item.deviceSerialNumber}`,
    fiscalAttribute: item.fiscalAttribute === '' ? 'нет ФП' : `ФП ${item.fiscalAttribute}`,
    confirmed: item.confirmed,
    confirmText: confirmTextOf(item),
    saleText: saleTextOf(item),
    returnedText:
      item.returnedKopecks > 0
        ? `возвращено ${formatKopecks(item.returnedKopecks)}`
        : refundsKnown
          ? 'возвратов нет'
          : 'возвраты не рассчитаны',
    returned: item.returnedKopecks > 0,
    refunds: item.refunds.map((refund) => ({ id: refund.id, label: `чек возврата ${formatInstant(refund.at)}`, day: isoDayOf(new Date(refund.at)) })),
    note: paymentsDiffer(item) ? 'Наличные и безналичные не равны итогу чека' : null,
  }
}

function journalVmOf(query: QueryResultLike<JournalJob | null>, failure: SyncFailure | null): ReceiptsJournalVm {
  const job = query.data ?? null
  const view = queryView(query, () => false)
  const status = job?.status ?? ''
  const known = job !== null
  const active = status === 'Pending' || status === 'Running'
  const statusText = !known
    ? 'Сверок журнала ещё не было'
    : status === 'Pending'
      ? 'Сверка ожидает запуска'
      : status === 'Running'
        ? 'Идёт сверка журнала'
        : status === 'Succeeded'
          ? 'Сверка выполнена'
          : status === 'Failed'
            ? 'Сверка не удалась'
            : `Сверка: ${status}`
  return {
    query: view,
    known,
    statusText,
    syncedAtText: job === null ? 'нет' : formatInstant(job.syncedAt, 'нет'),
    active,
    failed: status === 'Failed',
    error: job?.error ?? null,
    detail: job === null ? '' : `добавлено ${job.added}, связано с продажами ${job.linked}`,
    failure,
  }
}

const EMPTY_PARKED_ROWS: readonly ParkedRowVm[] = []

function parkedRow(sale: AqsiSale): ParkedRowVm {
  return {
    saleId: sale.id,
    version: sale.version,
    titleText: `${formatKopecks(sale.totalKopecks)} · ${formatInstant(sale.updatedAt)}`,
    stateText: `Деньги: ${MONEY[sale.payment].text} · Чек: ${RECEIPT[sale.fiscal].text}`,
    noteText: sale.lastMessage !== null && sale.lastMessage.trim() !== '' ? sale.lastMessage : null,
    lines: attentionLines(sale.attention),
    critical: sale.attention.some((item) => item.level === 'critical'),
  }
}

const EMPTY_ATTENTION_ROWS: readonly AttentionRowVm[] = []

function attentionRow(sale: AqsiAttentionSale): AttentionRowVm {
  return {
    saleId: sale.saleId,
    deviceText: `Касса ${sale.deviceId}`,
    titleText: `${formatKopecks(sale.totalKopecks)} · ${formatInstant(sale.updatedAt)}`,
    stateText: `Деньги: ${MONEY[sale.payment].text} · Чек: ${RECEIPT[sale.fiscal].text}`,
    lines: attentionLines(sale.attention),
    critical: sale.attention.some((item) => item.level === 'critical'),
    finished: sale.isFinished,
    latePayment: sale.attention.some((item) => item.code === LATE_PAYMENT_SLIP),
  }
}

export const makeSelectReceiptsVm = () => {
  const selectListView = createSelector(
    [
      (input: ReceiptsVmInput) => input.list.isUninitialized,
      (input: ReceiptsVmInput) => input.list.isLoading,
      (input: ReceiptsVmInput) => input.list.isError,
      (input: ReceiptsVmInput) => input.list.isSuccess,
      (input: ReceiptsVmInput) => input.list.error,
      (input: ReceiptsVmInput) => input.list.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        // Пустая страница с продолжением — не «пусто»: узкий отбор может дать её посреди выдачи.
        (page) => page.items.length === 0 && page.nextCursor === null,
      ),
  )
  const selectRows = createSelector(
    [(input: ReceiptsVmInput) => input.list.data],
    (page): readonly ReceiptRowVm[] =>
      page === undefined ? EMPTY_ROWS : page.items.map((item) => toRow(item, page.state.refundsKnown)),
  )
  const selectRegistersView = createSelector(
    [
      (input: ReceiptsVmInput) => input.registers.isUninitialized,
      (input: ReceiptsVmInput) => input.registers.isLoading,
      (input: ReceiptsVmInput) => input.registers.isError,
      (input: ReceiptsVmInput) => input.registers.isSuccess,
      (input: ReceiptsVmInput) => input.registers.error,
      (input: ReceiptsVmInput) => input.registers.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        (registers) => registers.registers.length === 0,
      ),
  )
  /** Дерево «Где смотрим»: выбор — применённый отбор, а не черновик формы (узлы применяются сразу). */
  const selectNav = createSelector(
    [
      selectRegistersView,
      (input: ReceiptsVmInput) => input.registers.data,
      (input: ReceiptsVmInput) => input.shifts.data,
      (input: ReceiptsVmInput) => input.view.applied,
      (input: ReceiptsVmInput) => input.view.collapsed,
      (input: ReceiptsVmInput) => input.summary.data?.registers,
      (input: ReceiptsVmInput) => input.shifts,
    ],
    (query, registers, shifts, applied, collapsed, registerCounts, shiftsQuery): ReceiptsNavVm => ({
      query,
      nodes: treeNodesOf(
        registers,
        shifts,
        applied,
        collapsed ?? NO_COLLAPSED,
        registerCounts === undefined ? null : new Map(registerCounts.map((item) => [item.serialNumber, item.count])),
      ),
      selectedId: selectedNodeOf(applied),
      summaryText: selectionText(registers, applied),
      stores: [{ id: 'all', label: 'Все магазины и кассы' }, ...[...new Set((registers?.registers ?? []).map((item) => item.shop).filter((name) => name !== '' && name !== NO_SHOP))].sort().map((name) => ({ id: shopNode(name), label: name }))],
      ...(shifts === undefined ? {} : { shifts }),
      ...(applied.registerId === '' ? {} : { shiftsQuery: queryView(shiftsQuery, (data) => data.shifts.length === 0) }),
    }),
  )
  const selectPeriod = createSelector(
    [(input: ReceiptsVmInput) => input.view.applied, (input: ReceiptsVmInput) => input.today],
    (applied, today): ReceiptsPeriodVm => {
      const effective = effectiveFilter(applied, today)
      const shift = applied.shiftNumber !== ''
      return {
        today,
        range: shift ? null : { from: effective.dateFrom, to: effective.dateTo },
        shiftNote: shift ? `Смена № ${applied.shiftNumber}: показаны её чеки за всё время смены. Выбор дат переключает отбор на период.` : null,
        listShown: listShownOf(applied, today),
      }
    },
  )
  const selectSummaryView = createSelector(
    [
      (input: ReceiptsVmInput) => input.summary.isUninitialized,
      (input: ReceiptsVmInput) => input.summary.isLoading,
      (input: ReceiptsVmInput) => input.summary.isError,
      (input: ReceiptsVmInput) => input.summary.isSuccess,
      (input: ReceiptsVmInput) => input.summary.error,
      (input: ReceiptsVmInput) => input.summary.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        () => false,
      ),
  )
  const selectStats = createSelector(
    [
      selectSummaryView,
      (input: ReceiptsVmInput) => input.summary.data,
      (input: ReceiptsVmInput) => input.monthSummary,
      (input: ReceiptsVmInput) => input.view.applied.kind,
      (input: ReceiptsVmInput) => input.view.applied.cashier,
      (input: ReceiptsVmInput) => input.view.applied,
      (input: ReceiptsVmInput) => input.today,
    ],
    (query, data, monthly, kind, cashier, applied, today): ReceiptsStatsVm => ({
      trendQuery: monthly === undefined ? query : queryView(monthly, () => false),
      query,
      items: statCardsOf(query.status === 'ready' && data !== undefined ? digestOf(data) : undefined, { money: formatKopecks, periodDays: periodDaysOf(applied, today) }),
      days: query.status === 'ready' && data !== undefined ? activeDaysOf(digestOf(data), formatKopecks) : NO_DAYS,
      kinds: [
        ...kindChipsOf(query.status === 'ready' && data !== undefined ? digestOf(data) : undefined),
        { id: 'unconfirmed', label: 'Не подтверждённые' },
      ],
      kind,
      cashiers: cashierChipsOf(query.status === 'ready' && data !== undefined ? digestOf(data) : undefined, cashier),
      cashier,
      trend: monthly?.data !== undefined && monthly.isSuccess
        ? trendOf(digestOf(monthly.data).days, firstOfMonth(effectiveFilter(applied, today).dateFrom || today),
          lastOfMonth(effectiveFilter(applied, today).dateFrom || today), formatKopecks, 100)
        : monthly === undefined && query.status === 'ready' && data !== undefined && effectiveFilter(applied, today).dateFrom !== ''
          ? trendOf(digestOf(data).days, effectiveFilter(applied, today).dateFrom, effectiveFilter(applied, today).dateTo, formatKopecks, 100) : NO_TREND,
      periodTrend: query.status === 'ready' && data !== undefined && effectiveFilter(applied, today).dateFrom !== ''
        ? trendOf(digestOf(data).days, effectiveFilter(applied, today).dateFrom, effectiveFilter(applied, today).dateTo, formatKopecks, 100) : NO_TREND,
    }),
  )
  const selectJournal = createSelector(
    [
      (input: ReceiptsVmInput) => input.journal,
      (input: ReceiptsVmInput) => input.syncFailure,
      (input: ReceiptsVmInput) => input.syncing === true,
    ],
    (journal, failure, syncing) => ({ ...journalVmOf(journal, failure), active: journalVmOf(journal, failure).active || syncing }),
  )

  const selectAttentionView = createSelector(
    [
      (input: ReceiptsVmInput) => input.attention?.isUninitialized ?? true,
      (input: ReceiptsVmInput) => input.attention?.isLoading ?? false,
      (input: ReceiptsVmInput) => input.attention?.isError ?? false,
      (input: ReceiptsVmInput) => input.attention?.isSuccess ?? false,
      (input: ReceiptsVmInput) => input.attention?.error,
      (input: ReceiptsVmInput) => input.attention?.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        (sales) => sales.length === 0,
      ),
  )
  const selectParkedView = createSelector(
    [
      (input: ReceiptsVmInput) => input.parked?.isUninitialized ?? true,
      (input: ReceiptsVmInput) => input.parked?.isLoading ?? false,
      (input: ReceiptsVmInput) => input.parked?.isError ?? false,
      (input: ReceiptsVmInput) => input.parked?.isSuccess ?? false,
      (input: ReceiptsVmInput) => input.parked?.error,
      (input: ReceiptsVmInput) => input.parked?.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        (sales) => sales.length === 0,
      ),
  )
  const selectParked = createSelector(
    [
      selectParkedView,
      (input: ReceiptsVmInput) => input.parked?.data,
      (input: ReceiptsVmInput) => input.seatReady ?? false,
      (input: ReceiptsVmInput) => input.unparkFeedback ?? null,
    ],
    (query, data, seatReady, feedback): ParkedVm => {
      const shown = query.status === 'ready' && data !== undefined
      const unparkFeedback = feedback?.command === 'sale.unpark' ? feedback : null
      return {
        query,
        rows: shown ? data.map(parkedRow) : EMPTY_PARKED_ROWS,
        count: shown ? data.length : null,
        returnBlockedText: seatReady ? null : 'Вернуть на кассу можно, начав работу за кассой в окне РМК',
        feedbackText: unparkFeedback === null ? null : (unparkFeedback.error?.message ?? unparkFeedback.note),
        feedbackPhase: unparkFeedback?.phase ?? null,
        returning: unparkFeedback?.phase === 'sending',
      }
    },
  )
  const selectAttention = createSelector(
    [selectAttentionView, (input: ReceiptsVmInput) => input.attention?.data],
    (query, data): AttentionVm => {
      const shown = query.status === 'ready' && data !== undefined
      return { query, rows: shown ? data.map(attentionRow) : EMPTY_ATTENTION_ROWS, count: shown ? data.length : null }
    },
  )

  return createSelector(
    [
      (input: ReceiptsVmInput) => input.resourceId,
      (input: ReceiptsVmInput) => input.view,
      (input: ReceiptsVmInput) => input.list.data,
      selectListView,
      selectRows,
      selectNav,
      selectPeriod,
      selectStats,
      selectJournal,
      selectAttention,
      (input: ReceiptsVmInput) => input.attentionAllowed ?? true,
      selectParked,
      (input: ReceiptsVmInput) => (input.summary.isSuccess ? input.summary.data : undefined),
    ],
    (resourceId, view, page, list, rows, nav, period, stats, journal, attention, attentionAllowed, parked, summary): ReceiptsVm => ({
      resourceId,
      tab: view.tab ?? 'journal',
      nav,
      period,
      stats,
      attention,
      attentionAllowed,
      parked,
      title: page?.state.title ?? 'Чеки AQSI',
      list,
      rows,
      page: {
        number: view.page + 1,
        hasPrevious: view.page > 0,
        nextCursor: page?.nextCursor ?? null,
        complete: page?.complete ?? false,
        totalCount: page?.totalCount ?? 0,
      },
      warning: page?.state.warning ?? null,
      originText: page !== undefined && period.listShown
        ? originTextOf(page.state.origin, page.state.readAt)
        : originTextOf(summary?.origin, summary?.readAt),
      summaryWarning: summary?.warning != null && summary.warning !== (period.listShown ? page?.state.warning : undefined) ? summary.warning : null,
      sourceMessage: page !== undefined && !page.state.available ? page.state.message : null,
      syncedAtText: page === undefined ? 'нет' : formatInstant(page.state.syncedAt, 'нет'),
      draft: view.draft,
      dirty: !sameFilter(view.draft, view.applied),
      cursorProblem: list.error !== null && CURSOR_ERROR_CODES.has(list.error.code),
      journal,
    }),
  )
}

/* ---------- детализация ---------- */

function lineVm(line: ReceiptDetail['lines'][number]): ReceiptLineVm {
  return {
    ordinal: line.ordinal,
    name: line.name,
    quantityText: formatQuantity(line.quantity),
    priceText: formatKopecks(line.priceKopecks),
    amountText: formatKopecks(line.amountKopecks),
    vatText: `код ${line.vatRateCode}`,
    subjectText: `код ${line.subjectCode}`,
    unitText: line.unitCode === 0 ? 'не указана (код 0)' : `код ${line.unitCode}`,
    barcode: line.barcode,
    returnedText: line.returnedQuantity > 0 ? formatQuantity(line.returnedQuantity) : null,
    goodKey: line.externalId,
  }
}

/** UC1d: можно ли начать возврат по чеку. Решает служба — это подсказка до нажатия, отказ покажется дословно. */
function refundEntryOf(data: ReceiptDetail, refundsKnown: boolean): { available: boolean; reason: string | null } | null {
  const receipt = data.receipt
  if (receipt.isRefund) return null
  let reason: string | null = null
  if (!data.isFiscal) reason = 'Чек не фискальный — возврат только по фискальному чеку'
  else if (refundsKnown && receipt.returnedKopecks >= receipt.totalKopecks) reason = 'Чек уже возвращён полностью'
  return { available: reason === null, reason }
}

export const makeSelectReceiptDetailVm = () =>
  createSelector(
    [
      (input: ReceiptDetailVmInput) => input.query.isUninitialized,
      (input: ReceiptDetailVmInput) => input.query.isLoading,
      (input: ReceiptDetailVmInput) => input.query.isError,
      (input: ReceiptDetailVmInput) => input.query.isSuccess,
      (input: ReceiptDetailVmInput) => input.query.error,
      (input: ReceiptDetailVmInput) => input.query.data,
      (input: ReceiptDetailVmInput) => input.refundsKnown === true,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data, refundsKnown): ReceiptDetailVm => {
      const query = queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        () => false,
      )
      // Ошибка при старых данных — ошибка, а не старый чек как актуальный (F06).
      if (query.status !== 'ready' || data === undefined) return { query, detail: null }
      const receipt = data.receipt
      const linesTotal = data.lines.reduce((sum, line) => sum + line.amountKopecks, 0)
      const notes: string[] = []
      if (!receipt.confirmed) notes.push('Чек не подтверждён кабинетом: известен только локальный журнал.')
      if (paymentsDiffer(receipt)) {
        notes.push(
          `Наличные (${formatKopecks(receipt.cashKopecks)}) и безналичные (${formatKopecks(receipt.cashlessKopecks)}) не равны итогу чека (${formatKopecks(receipt.totalKopecks)}).`,
        )
      }
      if (data.lines.length > 0 && linesTotal !== receipt.totalKopecks) {
        notes.push(`Сумма позиций (${formatKopecks(linesTotal)}) не равна итогу чека (${formatKopecks(receipt.totalKopecks)}).`)
      }
      if (data.isFiscal && receipt.fiscalAttribute === '') notes.push('Фискальный чек без фискального признака.')
      return {
        query,
        detail: {
          id: receipt.id,
          atText: formatInstant(receipt.at),
          kindText: receipt.isRefund ? 'Возврат' : 'Продажа',
          totalText: formatKopecks(receipt.totalKopecks),
          cashText: formatKopecks(receipt.cashKopecks),
          cashlessText: formatKopecks(receipt.cashlessKopecks),
          shiftText: receipt.shiftNumber === null ? 'не указана' : String(receipt.shiftNumber),
          docText: receipt.docNumber === null ? 'не указан' : String(receipt.docNumber),
          fiscalAttribute: receipt.fiscalAttribute === '' ? 'нет' : receipt.fiscalAttribute,
          fiscalText: data.isFiscal ? 'Фискальный документ' : 'Не фискальный документ',
          cashier: receipt.cashierName === '' ? 'не указан' : receipt.cashierName,
          device: receipt.deviceSerialNumber === '' ? 'не указана' : receipt.deviceSerialNumber,
          cardSlip: data.cardSlipId ?? 'нет',
          originText: data.origin === 'cabinet' ? originTextOf(data.origin, null) : null,
          confirmText: confirmTextOf(receipt),
          saleText: saleTextOf(receipt),
          saleId: receipt.saleId,
          returnedText:
            receipt.returnedKopecks > 0
              ? formatKopecks(receipt.returnedKopecks)
              : refundsKnown
                ? 'возвратов нет'
                : 'не рассчитано (сервер пока не связывает возвраты с чеком)',
          sourceId: receipt.sourceReceiptId,
          refunds: receipt.refunds.map((refund) => ({ id: refund.id, label: `Чек возврата № ${refund.id} · ${formatInstant(refund.at)}` })),
          notes,
          lines: data.lines.map(lineVm),
          refund: refundEntryOf(data, refundsKnown),
        },
      }
    },
  )
